import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Alert,
  Card,
  CardContent,
  Chip
} from '@mui/material';

const wireGauges = [
  {
    gauge: '12',
    label: '12 AWG',
    description: 'Heavy duty - Up to 20A',
    maxCurrent: '20A',
    applications: ['High power equipment', 'Stage lighting', 'Power distribution'],
    color: '#d32f2f' // Red for heavy duty
  },
  {
    gauge: '14', 
    label: '14 AWG',
    description: 'Standard duty - Up to 15A',
    maxCurrent: '15A',
    applications: ['General purpose', 'Audio equipment', 'Standard power'],
    color: '#f57c00' // Orange for standard
  },
  {
    gauge: '16',
    label: '16 AWG',
    description: 'Light duty - Up to 13A',
    maxCurrent: '13A', 
    applications: ['Small equipment', 'LED lighting', 'Low power devices'],
    color: '#388e3c' // Green for light duty
  }
];

const wireColors = [
  { value: 'black', label: 'Black', hex: '#000000' },
  { value: 'white', label: 'White', hex: '#ffffff' }
];

// Wire gauge and color availability matrix
const wireAvailability = {
  '12': ['black', 'white'],  // 12 AWG available in black and white
  '14': ['black'],           // 14 AWG only available in black  
  '16': ['black', 'white']   // 16 AWG available in black and white
};

// Check if connector is IEC type (not compatible with 12 AWG)
function isIECConnector(connector) {
  if (!connector) return false;
  return connector.family?.includes('IEC') || 
         connector.label?.toLowerCase().includes('iec') ||
         connector.id?.includes('IEC');
}

function WireGaugeSelection({ 
  value = { gauge: '', color: 'black' }, 
  onChange, 
  connectors = {} // Pass in the selected connectors to check for IEC
}) {
  const [selectedGauge, setSelectedGauge] = useState(value.gauge || '');
  const [selectedColor, setSelectedColor] = useState(value.color || 'black');

  // Check if any connector is IEC (12 AWG not compatible)
  const hasIECConnector = Object.values(connectors).some(connector => 
    isIECConnector(connector)
  );

  useEffect(() => {
    if (onChange) {
      onChange({ gauge: selectedGauge, color: selectedColor });
    }
  }, [selectedGauge, selectedColor, onChange]);

  const isGaugeDisabled = (gauge) => {
    // 12 AWG is not compatible with IEC connectors
    return gauge === '12' && hasIECConnector;
  };

  const isColorDisabled = (color) => {
    // If no gauge selected, all colors available
    if (!selectedGauge) return false;
    // Check if this color is available for the selected gauge
    return !wireAvailability[selectedGauge]?.includes(color);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', p: 2 }}>
      <Typography variant="h5" component="h2" gutterBottom align="center">
        Select Wire Gauge & Color
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph align="center">
        Choose the wire gauge and jacket color for your power cable
      </Typography>

      {hasIECConnector && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Note:</strong> IEC connectors detected. 12 AWG wire is not compatible with IEC connectors due to size constraints.
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>Available Colors:</strong> Black (12, 14, 16 AWG) • White (12, 16 AWG only)
      </Alert>

      {/* Wire Gauge Selection */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Wire Gauge (AWG)
        </Typography>
        <Grid container spacing={2}>
          {wireGauges.map((wire) => (
            <Grid item xs={12} md={4} key={wire.gauge}>
              <Card
                sx={{
                  height: '100%',
                  cursor: isGaugeDisabled(wire.gauge) ? 'not-allowed' : 'pointer',
                  opacity: isGaugeDisabled(wire.gauge) ? 0.5 : 1,
                  border: selectedGauge === wire.gauge ? 2 : 1,
                  borderColor: selectedGauge === wire.gauge ? 'primary.main' : 'divider',
                  '&:hover': {
                    boxShadow: isGaugeDisabled(wire.gauge) ? 1 : 4,
                    borderColor: isGaugeDisabled(wire.gauge) ? 'divider' : 'primary.main'
                  }
                }}
                onClick={() => {
                  if (!isGaugeDisabled(wire.gauge)) {
                    setSelectedGauge(wire.gauge);
                    // Check if current color is compatible with new gauge selection
                    if (selectedColor && !wireAvailability[wire.gauge]?.includes(selectedColor)) {
                      // Current color is not available for this gauge, reset to black
                      setSelectedColor('black');
                    }
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" sx={{ color: wire.color, fontWeight: 'bold' }}>
                      {wire.label}
                    </Typography>
                    <Chip 
                      label={wire.maxCurrent} 
                      color="primary" 
                      size="small" 
                      sx={{ ml: 'auto' }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {wire.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Applications:</strong> {wire.applications.join(', ')}
                  </Typography>
                  {isGaugeDisabled(wire.gauge) && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                      Not compatible with IEC connectors
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Wire Color Selection */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Jacket Color
        </Typography>
        {selectedGauge && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Available colors for {selectedGauge} AWG: {wireAvailability[selectedGauge]?.map(color => 
              wireColors.find(c => c.value === color)?.label
            ).join(', ')}
          </Typography>
        )}
        <Grid container spacing={1} justifyContent="center">
          {wireColors.map((color) => {
            const isDisabled = isColorDisabled(color.value);
            return (
              <Grid item key={color.value}>
                <Paper
                  sx={{
                    width: 60,
                    height: 60,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.3 : 1,
                    border: selectedColor === color.value ? 3 : 1,
                    borderColor: selectedColor === color.value ? 'primary.main' : 'divider',
                    backgroundColor: color.hex,
                    position: 'relative',
                    '&:hover': {
                      borderColor: isDisabled ? 'divider' : 'primary.main',
                      transform: isDisabled ? 'none' : 'scale(1.05)'
                    }
                  }}
                  onClick={() => !isDisabled && setSelectedColor(color.value)}
                >
                {/* Inner white circle for light colors to show selection */}
                {(color.value === 'white' || color.value === 'yellow') && (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      border: '2px solid #ccc',
                      backgroundColor: color.hex,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  />
                )}
                {/* Color name for very dark colors */}
                {color.value === 'black' && (
                  <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>
                    BLK
                  </Typography>
                )}
              </Paper>
              <Typography variant="caption" align="center" sx={{ display: 'block', mt: 0.5 }}>
                {color.label}
              </Typography>
            </Grid>
          );
        })}
        </Grid>
      </Box>

      {/* Selection Summary */}
      {selectedGauge && selectedColor && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <strong>Selected:</strong> {selectedGauge} AWG wire with {wireColors.find(c => c.value === selectedColor)?.label.toLowerCase()} jacket
        </Alert>
      )}


    </Box>
  );
}

export default WireGaugeSelection; 